import angularModule from 'Careers/angularModule/scripts/careers_module';
import template from 'Careers/angularModule/views/edit_career_profile/more_about_you_form.html';
import cacheAngularTemplate from 'cacheAngularTemplate';
import { setupBrandNameProperties } from 'AppBranding';
import { CareerProfileFormContext, MIN_AWARDS_AND_INTERESTS, TOP_LIST_SIZE } from 'FormConfigs';

const templateUrl = cacheAngularTemplate(angularModule, template);

angularModule.directive('moreAboutYouForm', [
    '$injector',
    function factory($injector) {
        const FormHelper = $injector.get('FormHelper');
        const TranslationHelper = $injector.get('TranslationHelper');
        const EditCareerProfileHelper = $injector.get('EditCareerProfileHelper');
        const Cohort = $injector.get('Cohort');

        return {
            restrict: 'E',
            // see STEP_FORM_ATTRS in edit_career_profile_dir.js
            scope: {
                // form-step-validity reads this by name off the scope. See STEP_FORM_ATTRS.
                $setValidity: '<setValidity',
                careerProfile: '<',
                currentUser: '<',
                formContext: '<',
                programType: '<',
                transientFormValues: '<',
            },
            templateUrl,
            link(scope) {
                // edit_career_profile_dir.js used to set these on the shared scope. This form's template
                // reads brandName and brandNameShort, so it sets them up itself now.
                setupBrandNameProperties($injector, scope, {
                    branding: () => scope.currentUser && Cohort.branding(scope.programType),
                });

                scope.isNonEnglish = FormHelper.isNonEnglish();
                scope.isStudentProfile = scope.formContext === CareerProfileFormContext.studentProfile;

                const translationHelper = new TranslationHelper('careers.edit_career_profile.more_about_you_form');

                EditCareerProfileHelper.logEventForApplication(scope.formContext, 'viewed-more_about_you');

                scope.programTitle = Cohort.standaloneShortProgramTitle(scope.programType);
                scope.isDegreeProgram = Cohort.isDegreeProgram(scope.programType);

                // The schema needs this to know whether primary_reason_for_applying is required. It
                // comes from the cohort rather than from either form model. See transientFormValues in
                // edit_career_profile_dir.js.
                scope.transientFormValues.is_degree_program = scope.isDegreeProgram;

                // The avatar input reads the user's avatar, while the career profile carries its own
                // denormalized copy that can be stale. Publish the value the input actually validates,
                // so the schema cannot pass on a copy while the user has no photo.
                Object.defineProperty(scope.transientFormValues, 'avatar_url', {
                    get() {
                        return scope.currentUser?.avatar_url;
                    },
                    configurable: true,
                    enumerable: true,
                });

                scope.topListSize = TOP_LIST_SIZE;
                scope.minAwardsAndInterests = MIN_AWARDS_AND_INTERESTS;

                scope.awardsPlaceholder = translationHelper.get('add_an_award');
                scope.professionalOrganizationPlaceholder = translationHelper.get(
                    'professional_organization_placeholder',
                );

                scope.reasonForApplyingOptionKeys = [
                    'start_own_company',
                    'advance_in_current_company',
                    'get_better_job',
                    'expand_business_knowledge',
                    'more_effective_leader',

                    // New options from https://trello.com/c/7ukMVFNq
                    'advance_in_career',
                    'network_with_top_tier_professionals',

                    // This option is at the bottom of the list on purpose since
                    // it's focused mainly towards Ivy Exec users registering from
                    // the /offer/ivy-exec dynamic landing page.
                    'complimentary_lessons',
                ];

                scope.attributeKeys = [
                    'creative',
                    'competitive',
                    'conscientious',
                    'loyal',
                    'resilient',
                    'optimistic',
                    'driven',
                    'flexible',
                    'balanced',
                    'persuasive',
                    'compassionate',
                    'courageous',
                    'motivating',
                    'meticulous',
                    'high_energy',
                ];

                scope.motivationKeys = [
                    'results',
                    'helping_others',
                    'career_advancement',
                    'innovation',
                    'fellowship',
                    'personal_growth',
                    'respect',
                    'security',
                ];

                scope.strengthKeys = [
                    'problem_solving',
                    'leadership',
                    'business_acumen',
                    'professionalism',
                    'entrepreneurial',
                    'relationship_building',
                    'work_ethic',
                    'design_aesthetic',
                    'communication',
                    'resourcefulness',
                ];

                FormHelper.supportForm(scope, scope.more_about_you);
                FormHelper.supportAutoSuggestOptions(scope);

                // NOTE: a `supportCheckboxGroups(scope, careerProfile, { race: scope.raceKeys })` call used to
                // sit here. `raceKeys` is defined only in demographics_form_section_dir.js, which has its own
                // isolate scope, so the value was always undefined and the call was a no-op. This form renders
                // no race inputs, so the call is gone.

                scope.professionalOrganizationsHandler = searchText =>
                    scope.getOptionsForType('professional_organization', searchText, undefined, {
                        membership_suggest: true,
                    });

                scope.getOptionsAndFilter = (type, searchText) =>
                    scope.getOptionsForType(type, searchText).then(options => {
                        // filter the response
                        const existingTexts = (scope.careerProfile[type] || []).map(item => item.text);

                        return options.filter(option => !existingTexts.includes(option.text));
                    });

                scope.getOptionsForAwardsAndInterests = searchText =>
                    scope.getOptionsAndFilter('awards_and_interests', searchText);
            },
        };
    },
]);
